import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { initializeFirebaseAnalytics } from "../config/firebase";
import {
  fetchVisitorApiData,
  initGoogleTagManager,
} from "../utils/visitorApi";
import { saveMyVisitorSnapshotAPI } from "../api/dashbordApi";

export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  functional: boolean;
  advertising: boolean;
}

export interface CookieConsentContextType {
  hasConsented: boolean;
  preferences: CookiePreferences;
  showBanner: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (preferences: CookiePreferences) => void;
  showPreferences: () => void;
  hideBanner: () => void;
}

const CookieConsentContext = createContext<
  CookieConsentContextType | undefined
>(undefined);

const COOKIE_CONSENT_KEY = "sugarcribs_cookie_consent";
const COOKIE_PREFERENCES_KEY = "sugarcribs_cookie_preferences";

const defaultPreferences: CookiePreferences = {
  essential: true, // Always true, cannot be disabled
  analytics: false,
  functional: false,
  advertising: false,
};

interface CookieConsentProviderProps {
  children: ReactNode;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({
  children,
}) => {
  const [hasConsented, setHasConsented] = useState<boolean>(false);
  const [preferences, setPreferences] =
    useState<CookiePreferences>(defaultPreferences);
  const [showBanner, setShowBanner] = useState<boolean>(false);

  // Load consent status and preferences from localStorage on mount
  useEffect(() => {
    const savedConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
    const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);

    if (savedConsent === "true") {
      setHasConsented(true);
      setShowBanner(false);

      if (savedPreferences) {
        try {
          const parsedPreferences = JSON.parse(savedPreferences);
          setPreferences(parsedPreferences);
        } catch (error) {
          console.error("Error parsing saved cookie preferences:", error);
          setPreferences(defaultPreferences);
        }
      }
    } else {
      // First-time visitor or no consent given
      setShowBanner(true);
      setHasConsented(false);
    }
  }, []);

  // Initialize analytics based on consent
  useEffect(() => {
    if (hasConsented && preferences.analytics) {
      initializeAnalytics();
    } else {
      disableAnalytics();
    }
  }, [hasConsented, preferences.analytics]);

  const initializeAnalytics = async () => {
    // Initialize Google Analytics if consent is given
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("consent", "update", {
        analytics_storage: "granted",
        ad_storage: preferences.advertising ? "granted" : "denied",
        functionality_storage: preferences.functional ? "granted" : "denied",
      });
    }

    const gtmContainerId = process.env.REACT_APP_GTM_CONTAINER_ID;
    if (typeof window !== "undefined" && gtmContainerId) {
      initGoogleTagManager(gtmContainerId);
    }

    const visitorProjectId = process.env.REACT_APP_VISITOR_API_PROJECT_ID;
    if (typeof window !== "undefined" && visitorProjectId) {
      try {
        const visitor = await fetchVisitorApiData(visitorProjectId);
        try {
          // Non-blocking persistence for admin/security dashboards.
          await (saveMyVisitorSnapshotAPI as any)({
            visitor,
            ipAddress: visitor?.ipAddress,
          });
        } catch {
          /* ignore — бекенд може бути ще не оновлений */
        }
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "visitor_api_enriched",
          visitor_country_code: visitor.countryCode,
          visitor_country_name: visitor.countryName,
          visitor_region: visitor.region,
          visitor_city: visitor.city,
          visitor_os: visitor.os,
          visitor_browser: visitor.browser,
        });
      } catch {
        /* ignore — мережева помилка або ліміт API */
      }
    }

    // Initialize Firebase Analytics if consent is given
    try {
      await initializeFirebaseAnalytics();
    } catch (error) {
      console.warn("Failed to initialize Firebase Analytics:", error);
    }
  };

  const disableAnalytics = () => {
    // Disable Google Analytics
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("consent", "update", {
        analytics_storage: "denied",
        ad_storage: "denied",
        functionality_storage: "denied",
      });
    }
  };

  const acceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      functional: true,
      advertising: true,
    };

    setPreferences(allAccepted);
    setHasConsented(true);
    setShowBanner(false);

    // Save to localStorage
    localStorage.setItem(COOKIE_CONSENT_KEY, "true");
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(allAccepted));

    // Initialize analytics immediately
    initializeAnalytics();
  };

  const rejectAll = () => {
    const rejected: CookiePreferences = {
      essential: true, // Essential cookies cannot be disabled
      analytics: false,
      functional: false,
      advertising: false,
    };

    setPreferences(rejected);
    setHasConsented(true);
    setShowBanner(false);

    // Save to localStorage
    localStorage.setItem(COOKIE_CONSENT_KEY, "true");
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(rejected));

    // Disable analytics
    disableAnalytics();
  };

  const savePreferences = (newPreferences: CookiePreferences) => {
    // Ensure essential cookies are always enabled
    const finalPreferences: CookiePreferences = {
      ...newPreferences,
      essential: true,
    };

    setPreferences(finalPreferences);
    setHasConsented(true);
    setShowBanner(false);

    // Save to localStorage
    localStorage.setItem(COOKIE_CONSENT_KEY, "true");
    localStorage.setItem(
      COOKIE_PREFERENCES_KEY,
      JSON.stringify(finalPreferences)
    );

    // Update analytics based on new preferences
    if (finalPreferences.analytics) {
      initializeAnalytics();
    } else {
      disableAnalytics();
    }
  };

  const showPreferences = () => {
    setShowBanner(true);
  };

  const hideBanner = () => {
    setShowBanner(false);
  };

  const value: CookieConsentContextType = {
    hasConsented,
    preferences,
    showBanner,
    acceptAll,
    rejectAll,
    savePreferences,
    showPreferences,
    hideBanner,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = (): CookieConsentContextType => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error(
      "useCookieConsent must be used within a CookieConsentProvider"
    );
  }
  return context;
};

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    firebase: any;
    dataLayer?: Record<string, unknown>[];
  }
}
